import React, { Fragment, useContext, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import optimizely, { optimizelyFeatures } from 'api/optimizely';
import * as cssVariable from 'cssVariables';
import { getImageWithResamplingQuery } from 'util/resampling';
import { isMobile, isTouch } from 'util/device';

import PlayIcon from 'components/svg/Play';
import SpeakerIcon from 'components/svg/Speaker';
import StopIcon from 'components/svg/Stop';

import PlayControl from 'components/PlayControl';
import { convertHMSToSeconds } from 'util/time';
import ClipProgress from './ClipProgress';
import Progressbar from './Progressbar';
import AirDate from './AirDate';
import Duration from './Duration';
import Description from './Description';
import LazyTileImage from 'components/LazyTileImage';
import ToggleFavoriteButton from 'components/ToggleFavoriteButton';
import CollectionContext from 'components/Collection/CollectionContext';
import CollectionModalContext from 'components/Collection/CollectionModal/CollectionModalContext';

import styles from './OnDemandTileList.styles';
import { createUseStyles } from 'react-jss';
import SocialShare from 'components/SocialShare';
import { useQuery } from 'components/hooks/useQuery';
import { SCROLL_CONTAINER_ID } from '../../../globalConst/const';

const fallbackUrl = '/album-cover-fallback-high-res.png';

const useStyles = createUseStyles(styles, { name: 'OnDemandTileList' });

const OnDemandTileList = ({
  isLoggedIn,
  isPlaying,
  isLoading,
  isActive,
  playerInitialized,
  onPlayPauseToggle,
  onDemandClip: {
    id,
    show,
    publishedUtc,
    publishDate,
    showNewLabel,
    title,
    description,
    disabled,
    image,
    imageUrl,
    track,
    duration,
    brandColor,
    index,
    progress,
    contentTypeId,
    podcast,
  },
  onDemandClipInPlayerTime,
  onDemandTitle,
  screenReaderTitle,
  station,
}) => {
  const classes = useStyles({ onDemandClip: { brandColor } });

  const isPodcast = !!podcast;
  const { isPodcastPage, isInFold } = useContext(CollectionContext);
  const { isInModal } = useContext(CollectionModalContext);

  let query = useQuery();
  const [isHighlighted, setHighlighted] = useState(false);

  const mobilePodcastCollection = isPodcast && isMobile && !isInModal;
  const usableImage = image && image.url ? image.url : imageUrl;

  const { durationLength, contentLength: clipDuration = convertHMSToSeconds(duration) } = progress || {};

  const [cachedProgress, setCachedProgress] = useState(0);

  let scrollTimeout;

  const scrollContainer = isTouch ? window : document.getElementById(SCROLL_CONTAINER_ID);

  const handleHighlightScroll = () => {
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(function () {
      setHighlighted(true);
      onPlayPauseToggle();
      scrollContainer.removeEventListener('scroll', handleHighlightScroll);
    }, 100);
  };

  useEffect(() => {
    if (id === query.get('id') && playerInitialized) {
      const tile = document.getElementById(`ondemand_tile_list-${id}`);
      scrollContainer.addEventListener('scroll', handleHighlightScroll);
      tile.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  }, [playerInitialized]);

  useEffect(() => {
    if (isHighlighted && !isActive) {
      setHighlighted(false);
    }
  }, [isActive]);

  useEffect(() => {
    // Reset progress on user state change
    setCachedProgress(0);
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    // Update current clip progress
    if (onDemandClipInPlayerTime) {
      setCachedProgress(onDemandClipInPlayerTime);
    }
  }, [onDemandClipInPlayerTime]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    // Set progress after is fetch
    if (!cachedProgress && durationLength) {
      setCachedProgress(durationLength);
    }
  }, [progress]);

  const getShareDescription = () => {
    const url = window.location.href;
    return `Luister de laatste aflevering van ${title} via ${url}`;
  };

  const clipProgress = isLoggedIn ? cachedProgress : onDemandClipInPlayerTime;
  return (
    <div
      id={`ondemand_tile_list-${id}`}
      data-testid="ondemand_tile_list"
      className={classNames(classes.listItem, {
        [classes.podcastEpisode]: isPodcastPage,
        [classes.highlight]: isHighlighted,
      })}
      role="presentation"
      onClick={disabled ? undefined : onPlayPauseToggle}
    >
      <div
        className={classNames(classes.rowContainer, {
          [classes.rowContainerEven]: index % 2 === 0 && !mobilePodcastCollection,
          [classes.withHoverEffect]: !isPodcast,
          [classes.hideTriangles]: isPlaying,
          [classes.mobileRowContainer]: mobilePodcastCollection,
          [classes.lightBackgound]: isInFold,
        })}
      >
        <div className={classes.clickContainer}>
          <LazyTileImage
            data-key="tile-image"
            className={classNames({
              [classes.albumCover]: !disabled,
              [classes.albumCoverWithColor]: brandColor,
            })}
            imageUrl={
              usableImage ? getImageWithResamplingQuery({ url: usableImage, isTransparent: true }) : fallbackUrl
            }
          >
            {!isPodcast && !disabled && (
              <div className={classes.triangles}>
                <div className={classes.triangleSmall} style={{ background: brandColor || cssVariable.black }} />
                <div className={classes.triangleBig} style={{ background: brandColor || cssVariable.black }} />
              </div>
            )}
            {!disabled ? (
              <div
                className={classNames(classes.albumCoverOverlay, { [classes.roundOverlay]: !isPlaying })}
                data-testid="onDemandTileList_play"
              >
                {isPlaying ? (
                  <Fragment>
                    <SpeakerIcon className={classes.speakerIcon} volumeLevel={100} />
                    <StopIcon filled className={classes.stopIcon} />
                  </Fragment>
                ) : (
                  <PlayIcon />
                )}
              </div>
            ) : (
              isPlaying && (
                <div className={classNames(classes.albumCoverOverlay)}>
                  <SpeakerIcon volumeLevel={100} />
                </div>
              )
            )}
          </LazyTileImage>
          <div className={classes.info}>
            {station && (
              <Fragment>
                <h3 className={classNames(classes.title, { [classes.titleActivePlayer]: isPlaying })}>
                  {onDemandTitle}
                </h3>
                <span className={classes.screenReaderTitle}>{screenReaderTitle}</span>
                <span className={classes.airDateHolder}>
                  <AirDate date={publishedUtc} />
                </span>
                {duration && (
                  <span className={classes.airDateHolder}>
                    , <Duration duration={duration} />
                  </span>
                )}
              </Fragment>
            )}
            {/* @Note: Used for podcast and ondemand tile */}
            {!station && !show && !track && (
              <Fragment>
                {isPodcastPage && (publishDate || publishedUtc) && (
                  <div className={classes.podcastAirDate}>
                    <AirDate date={publishDate || publishedUtc} showDays showNewLabel={showNewLabel} />
                  </div>
                )}
                <h3 className={classNames(classes.title, { [classes.titleActivePlayer]: isPlaying })}>{title}</h3>
                <span className={classes.screenReaderTitle}>{title}</span>
                {isPodcastPage && duration && (
                  <div className={classes.podcastDuration}>
                    <ClipProgress clipProgress={clipProgress} clipDuration={clipDuration} />
                  </div>
                )}
              </Fragment>
            )}

            {/* @Note: Not used (no show page currently) */}
            {show && show.hasPage && !disabled && !isMobile && !isInFold && (
              <NavLink
                className={classes.pageLink}
                to={`/radioshows/${show.slug}`}
                aria-label={`Ga naar ${show.name}`}
                title="Bekijk meer"
                onClick={(e) => e.stopPropagation()}
              >
                Bekijk meer
              </NavLink>
            )}
            {/* @Note: Used for ondemand content and old podcast design */}
            {!isPodcastPage && (publishDate || publishedUtc) && (
              <span className={classes.airDateHolder}>
                <AirDate date={publishDate || publishedUtc} />
                {duration && (
                  <Fragment>
                    <span> - </span>
                    <Duration duration={duration} />
                  </Fragment>
                )}
              </span>
            )}
          </div>
          {/* @Note: Play button for ondemand  */}
          {!isPodcastPage && !disabled && isInFold && (
            <PlayControl
              playToggle={onPlayPauseToggle}
              isPlaying={isPlaying && !isLoading}
              isLoading={isPlaying && isLoading}
              isOnDemand
              listItem
              size={45}
            />
          )}
          <div className={classes.buttonGroup}>
            {isPodcastPage &&
              isPodcast &&
              optimizely.isFeatureEnabled(optimizelyFeatures.FAVORITE_PODCAST_EPISODES) && (
                <ToggleFavoriteButton item={{ id, contentTypeId, title }} />
              )}
            <SocialShare
              params={{ id }}
              right
              subject={title}
              description={getShareDescription()}
              color={cssVariable.grey500}
            />
          </div>
        </div>
      </div>
      {isPodcastPage && <Description text={description} />}
      <Progressbar duration={clipDuration} progress={clipProgress} isPlaying={isPlaying} />
    </div>
  );
};

OnDemandTileList.propTypes = {
  isPlaying: PropTypes.bool,
  isLoading: PropTypes.bool,
  isActive: PropTypes.bool,
  playerInitialized: PropTypes.bool,
  onDemandClip: PropTypes.object,
  onDemandTitle: PropTypes.string,
  screenReaderTitle: PropTypes.string,
  station: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  onPlayPauseToggle: PropTypes.func.isRequired,
  onDemandClipInPlayerTime: PropTypes.number,
};

OnDemandTileList.defaultProps = {
  isPlaying: false,
  isLoading: false,
  isActive: false,
  playerInitialized: false,
  onDemandClip: null,
  onDemandTitle: null,
  screenReaderTitle: null,
  station: null,
  isLoggedIn: false,
  onDemandClipInPlayerTime: null,
};

export default OnDemandTileList;
